import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "sonner";
import { lenisRef, scrollToId, scrollTop } from "@/lib/nav";
import UtilityBar from "@/components/landing/UtilityBar";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Home from "@/pages/Home";
import ApparitionsIndex from "@/pages/ApparitionsIndex";
import ApparitionPage from "@/pages/ApparitionPage";
import CalendarPage from "@/pages/CalendarPage";
import EditorialStandards from "@/pages/EditorialStandards";
import EastAndWestPage from "@/pages/EastAndWestPage";
import ContactPage from "@/pages/ContactPage";
import PrayerPage from "@/pages/PrayerPage";
import RosaryPage from "@/pages/RosaryPage";
import RosaryPopoutPage from "@/pages/RosaryPopoutPage";
import PrayerRopePage from "@/pages/PrayerRopePage";
import SaintsPage from "@/pages/SaintsPage";
import SacredArtPage from "@/pages/SacredArtPage";
import ApologeticsPage from "@/pages/ApologeticsPage";
import AboutPage from "@/pages/AboutPage";
import DogmaPage from "@/pages/DogmaPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ParishPosterPage from "@/pages/ParishPosterPage";
import LibraryPage from "@/pages/LibraryPage";
import ChristosHub from "@/pages/ChristosHub";
import DevotionPage from "@/pages/DevotionPage";
import TwoHeartsPage from "@/pages/TwoHeartsPage";
import JesusDogmaPage from "@/pages/JesusDogmaPage";
import ConsecrationPage from "@/pages/ConsecrationPage";
import EnthronementPage from "@/pages/EnthronementPage";
import NotFoundPage from "@/pages/NotFoundPage";
import FirstSaturdaysPage from "@/pages/FirstSaturdaysPage";
import "@/components/accessibility/accessibility.css";

function SiteChrome({ children }) {
  const { pathname } = useLocation();
  return pathname === "/rosary/popout" ? null : children;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      let cancelled = false;
      const t = setTimeout(async () => {
        // Font metrics and the new route's height must settle before a deep jump.
        await document.fonts.ready;
        if (!cancelled) {
          const readingNav = document.querySelector('[data-testid="devotion-reading-navigation"]');
          const mobileRosary = pathname === "/rosary" && hash === "#pray" && window.matchMedia("(max-width: 1023px)").matches;
          const headerBar = document.querySelector('[data-testid="main-header"] > div');
          // Lenis already includes CSS scroll-margin; native fragment jumps use
          // that same margin. Only the no-Lenis fallback needs a manual offset.
          const offset = mobileRosary ? (lenisRef.current ? 0 : -(headerBar?.getBoundingClientRect().height || 0)) : readingNav ? -(parseFloat(getComputedStyle(readingNav).top) + readingNav.offsetHeight + 20) : -96;
          scrollToId(hash.slice(1), offset, { immediate: true });
        }
      }, 120);
      return () => { cancelled = true; clearTimeout(t); };
    }
    scrollTop(true);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <div className="App bg-ivory text-charcoal">
        <div className="grain-overlay" aria-hidden="true" />
        <Toaster position="bottom-right" richColors />
        <SiteChrome><UtilityBar /><Header /></SiteChrome>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/apparitions" element={<ApparitionsIndex />} />
          <Route path="/apparitions/:id" element={<ApparitionPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/editorial-standards" element={<EditorialStandards />} />
          <Route path="/east-and-west" element={<EastAndWestPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/prayer-and-devotion" element={<PrayerPage />} />
          <Route path="/prayer-and-devotion/first-saturdays" element={<FirstSaturdaysPage />} />
          <Route path="/rosary" element={<RosaryPage />} />
          <Route path="/rosary/popout" element={<RosaryPopoutPage />} />
          <Route path="/prayer-rope" element={<PrayerRopePage />} />
          <Route path="/saints" element={<SaintsPage />} />
          <Route path="/sacred-art" element={<SacredArtPage />} />
          <Route path="/apologetics" element={<ApologeticsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/parish-poster-kit" element={<ParishPosterPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/christ-the-king" element={<ChristosHub />} />
          <Route path="/christ-the-king/two-hearts" element={<TwoHeartsPage />} />
          <Route path="/christ-the-king/dogmas" element={<JesusDogmaPage />} />
          <Route path="/christ-the-king/dogmas/:dogmaSlug" element={<JesusDogmaPage />} />
          <Route path="/christ-the-king/consecration" element={<ConsecrationPage />} />
          <Route path="/christ-the-king/enthronement" element={<EnthronementPage />} />
          <Route path="/christ-the-king/:slug" element={<DevotionPage />} />
          <Route path="/dogmas" element={<DogmaPage />} />
          <Route path="/dogmas/:slug" element={<DogmaPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <SiteChrome><Footer /></SiteChrome>
      </div>
    </BrowserRouter>
  );
}
